import React, { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useCustomer, useUpdateCustomer, useCustomerOrders } from '../hooks/useQueries';
import './Account.css';

const norm = (s) => (s ?? '').toString().trim().toLowerCase();

// order.js now returns the full product_images array (tagged with variant_id
// and custom_option_id), product_variants, and customization_fields (with
// their options) on each order item's product — so we can match both the
// purchased color AND the purchased customization option entirely from this
// one response, no extra product fetch needed.
//
// item.variants                     → snapshot of purchased variant(s), e.g. "Green"
// item.customizations                → snapshot of purchased customization(s), e.g. field_label/field_value
// item.product.product_variants      → current variants for that product, gives us the id
// item.product.customization_fields  → current fields+options, gives us the option id
// item.product.product_images        → images, each tagged with variant_id / custom_option_id
const resolveOrderItemImage = (item) => {
  const images = item.product?.product_images ?? [];
  const primary = images.find((img) => img.is_primary) ?? images[0];
  const fallback = primary?.image_url ?? null;

  // Match the purchased color, if any
  const colorVariant = (item.variants || []).find(
    (v) => norm(v.variant_type_name) === 'color'
  );
  const matchedVariant = colorVariant
    ? (item.product?.product_variants || []).find(
        (v) =>
          norm(v.variant_name) === norm(colorVariant.variant_name) ||
          norm(v.variant_value) === norm(colorVariant.variant_value)
      )
    : null;

  // Match any purchased select/radio customization to its option id
  const productFields = item.product?.customization_fields ?? [];
  let matchedOptionId = null;
  (item.customizations || []).forEach((c) => {
    const field = productFields.find((f) => norm(f.field_label) === norm(c.field_label));
    const opt = (field?.customization_field_options || []).find(
      (o) => norm(o.option_value) === norm(c.field_value) || norm(o.option_label) === norm(c.field_value)
    );
    if (opt) matchedOptionId = opt.id;
  });

  // Prefer an image tagged with both the matched color and the matched option
  if (matchedVariant && matchedOptionId) {
    const both = images.find(
      (img) => img.variant_id === matchedVariant.id && img.custom_option_id === matchedOptionId
    );
    if (both) return both.image_url;
  }
  if (matchedVariant) {
    const colorImg = images.find((img) => img.variant_id === matchedVariant.id && !img.custom_option_id);
    if (colorImg) return colorImg.image_url;
  }
  if (matchedOptionId) {
    const optionImg = images.find((img) => img.custom_option_id === matchedOptionId && !img.variant_id);
    if (optionImg) return optionImg.image_url;
  }
  return fallback;
};

const STATUS_MAP = {
  pending: { label: 'Pending', color: '#c2410c', bg: '#fff7ed', border: '#fed7aa' },
  confirmed: { label: 'Confirmed', color: '#1d4ed8', bg: '#eff6ff', border: '#bfdbfe' },
  processing: { label: 'Processing', color: '#7c3aed', bg: '#faf5ff', border: '#e9d5ff' },
  completed: { label: 'Completed', color: '#15803d', bg: '#f0fdf4', border: '#bbf7d0' },
  delivered: { label: 'Delivered', color: '#047857', bg: '#ecfdf5', border: '#a7f3d0' },
  cancelled: { label: 'Cancelled', color: '#b91c1c', bg: '#fef2f2', border: '#fecaca' },
};

const TABS = ['Settings', 'Orders history'];

function StatusBadge({ status }) {
  const s = STATUS_MAP[status] ?? STATUS_MAP.pending;
  return (
    <span className="acc-status-badge" style={{ background: s.bg, color: s.color, border: `1px solid ${s.border}` }}>
      {s.label}
    </span>
  );
}

// ── Settings Tab (unchanged) ───────────────────────────────
function SettingsTab({ customer, customerId }) {
  const updateMutation = useUpdateCustomer();
  const [form, setForm] = useState({
    firstName: customer.first_name ?? '',
    lastName: customer.last_name ?? '',
    phone: customer.phone ?? '',
  });
  const [saved, setSaved] = useState(false);
  const [apiError, setApiError] = useState('');

  React.useEffect(() => {
    setForm({
      firstName: customer.first_name ?? '',
      lastName: customer.last_name ?? '',
      phone: customer.phone ?? '',
    });
  }, [customer.first_name, customer.last_name, customer.phone]);

  const set = (k, v) => { setForm(f => ({ ...f, [k]: v })); setApiError(''); };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.firstName.trim()) { setApiError('First name is required.'); return; }
    if (!form.phone.trim()) { setApiError('Phone number is required.'); return; }
    setApiError('');
    try {
      await updateMutation.mutateAsync({
        id: customerId, first_name: form.firstName.trim(),
        last_name: form.lastName.trim(), phone: form.phone.trim(),
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (err) {
      setApiError(err?.response?.data?.message || 'Something went wrong. Please try again.');
    }
  };

  const isSaving = updateMutation.isPending;

  return (
    <form className="acc-settings-form" onSubmit={handleSave} noValidate>
      <h2 className="acc-section-title">Account information</h2>
      <p className="acc-section-sub">Update your personal details below.</p>
      <div className="acc-avatar-row">
        <div className="acc-avatar-wrap" style={{ cursor: 'default' }}>
          <div className="acc-avatar-initials">
            {(form.firstName?.[0] ?? '').toUpperCase()}{(form.lastName?.[0] ?? '').toUpperCase()}
          </div>
        </div>
      </div>
      {apiError && (
        <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, padding: '10px 14px', marginBottom: 18, color: '#dc2626', fontSize: 13, display: 'flex', alignItems: 'center', gap: 8 }}>
          ⚠️ {apiError}
        </div>
      )}
      <div className="acc-fields">
        <div className="acc-field-group">
          <label className="acc-label">First name *</label>
          <input className="acc-input" value={form.firstName} onChange={e => set('firstName', e.target.value)} placeholder="Your first name" disabled={isSaving} />
        </div>
        <div className="acc-field-group">
          <label className="acc-label">Last name</label>
          <input className="acc-input" value={form.lastName} onChange={e => set('lastName', e.target.value)} placeholder="Your last name" disabled={isSaving} />
        </div>
        <div className="acc-field-group">
          <label className="acc-label">Email</label>
          <div className="acc-input-icon-wrap">
            <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" className="acc-input-icon"><rect x="2" y="4" width="20" height="16" rx="2" /><path d="m2 7 10 7 10-7" /></svg>
            <input readOnly disabled className="acc-input acc-input--icon" style={{ opacity: 0.6, cursor: 'not-allowed' }} value={customer.email ?? ''} type="email" />
          </div>
          <span style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>Email address cannot be changed.</span>
        </div>
        <div className="acc-field-group">
          <label className="acc-label">Phone number *</label>
          <div className="acc-input-icon-wrap">
            <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" className="acc-input-icon"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 13 19.79 19.79 0 0 1 1.61 4.38 2 2 0 0 1 3.6 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" /></svg>
            <input className="acc-input acc-input--icon" value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="+91 98765 43210" type="tel" disabled={isSaving} />
          </div>
        </div>
      </div>
      <button type="submit" className={`acc-save-btn ${saved ? 'acc-save-btn--saved' : ''}`} disabled={isSaving} style={{ opacity: isSaving ? 0.7 : 1 }}>
        {isSaving ? 'Saving…' : saved ? '✓ Saved!' : 'Update account'}
      </button>
    </form>
  );
}

// ── Orders Tab ─────────────────────────────────────────────
function OrdersTab({ customerId }) {
  const { data: ordersRes, isLoading, isError } = useCustomerOrders(customerId);
  const orders = ordersRes?.data?.data ?? [];

  if (isLoading) {
    return (
      <div className="acc-orders">
        <h2 className="acc-section-title">Order history</h2>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {[1, 2].map(i => (
            <div key={i} style={{ border: '1.5px solid var(--border)', borderRadius: 16, overflow: 'hidden' }}>
              <div style={{ padding: '18px 24px', background: 'var(--bg-warm)', borderBottom: '1px solid var(--border)' }}>
                <div style={{ height: 14, width: '30%', background: '#e8e0d6', borderRadius: 6, marginBottom: 8 }} />
                <div style={{ height: 12, width: '20%', background: '#e8e0d6', borderRadius: 6 }} />
              </div>
              <div style={{ padding: '20px 24px' }}>
                <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
                  <div style={{ width: 72, height: 80, borderRadius: 10, background: '#f0ebe4' }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ height: 14, width: '50%', background: '#e8e0d6', borderRadius: 6, marginBottom: 8 }} />
                    <div style={{ height: 12, width: '30%', background: '#e8e0d6', borderRadius: 6 }} />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="acc-orders">
        <h2 className="acc-section-title">Order history</h2>
        <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 10, padding: '20px 24px', color: '#dc2626', fontSize: 14 }}>
          ⚠️ Could not load your orders. Please refresh the page.
        </div>
      </div>
    );
  }

  if (!orders.length) {
    return (
      <div className="acc-empty-orders">
        <div className="acc-empty-icon">📦</div>
        <h3>No orders yet</h3>
        <p>When you place an order, it will appear here.</p>
        <Link to="/products" className="btn btn-primary-warm">Browse Gifts</Link>
      </div>
    );
  }

  return (
    <div className="acc-orders">
      <h2 className="acc-section-title">Order history</h2>
      <p className="acc-section-sub">
        Check the status of recent orders and discover similar products.
      </p>
      <div className="acc-orders-list">
        {orders.map(order => {
          const items = order.items ?? [];
          const placedDate = new Date(order.createdAt).toLocaleDateString('en-IN', {
            day: 'numeric', month: 'long', year: 'numeric',
          });
          const total = Number(order.total_amount ?? 0);

          return (
            <div key={order.id} className="acc-order-card">

              {/* ── Header ── */}
              <div className="acc-order-header">
                <div className="acc-order-meta">
                  <span className="acc-order-id">#{order.id}</span>
                  <StatusBadge status={order.status} />
                  <span className="acc-order-date">Placed on {placedDate}</span>
                </div>
                <div className="acc-order-actions">
                  <Link
                    to={`/my-orders/${order.id}`}
                    className="acc-order-btn acc-order-btn--outline"
                  >
                    View order
                  </Link>
                </div>
              </div>

              {/* ── Items ── */}
              <div className="acc-order-items">
                {items.map((item, i) => {
                  const productTitle = item.product?.title ?? `Product #${item.product_id}`;
                  const imageUrl = resolveOrderItemImage(item);
                  const itemTotal = Number(item.total_price ?? 0);

                  return (
                    <div key={item.id ?? i} className="acc-order-item">

                      {/* Image */}
                      <div className="acc-item-img-wrap">
                        {imageUrl ? (
                          <img
                            src={imageUrl}
                            alt={productTitle}
                            className="acc-item-img"
                            onError={e => {
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'flex';
                            }}
                          />
                        ) : null}
                        <div style={{
                          display: imageUrl ? 'none' : 'flex',
                          width: '100%', height: '100%',
                          background: 'var(--bg-warm)',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: 28,
                        }}>🎁</div>
                      </div>

                      {/* Info */}
                      <div className="acc-item-info">
                        <p className="acc-item-name">{productTitle}</p>
                        <div className="acc-item-meta">
                          <span>₹{item.base_price}</span>
                        </div>
                        <div className="acc-item-meta">
                          <span>Qty {item.quantity}</span>
                          {Number(item.addon_price) > 0 && (
                            <>
                              <span className="acc-item-sep">·</span>
                              <span>Add-ons: ₹{Number(item.addon_price).toLocaleString('en-IN')}</span>
                            </>
                          )}
                        </div>
                      </div>

                      {/* Price */}
                      <div className="acc-item-right">
                        <span className="acc-item-price">
                          ₹{itemTotal.toLocaleString('en-IN')}
                        </span>
                      </div>

                    </div>
                  );
                })}
              </div>

              {/* ── Footer ── */}
              <div className="acc-order-footer">
                <span className="acc-order-total-label">Order Total</span>
                <span className="acc-order-total">
                  ₹{total.toLocaleString('en-IN')}
                </span>
              </div>

            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Loading Skeleton ───────────────────────────────────────
function SettingsSkeleton() {
  const bar = (w, h = 14) => ({
    background: 'linear-gradient(90deg, #f0f0f0 25%, #e8e8e8 50%, #f0f0f0 75%)',
    backgroundSize: '800px 100%',
    animation: 'shimmer 1.4s infinite',
    borderRadius: 8, height: h, width: w, marginBottom: 8,
  });
  return (
    <div className="acc-settings-form">
      <div style={bar('40%', 24)} />
      <div style={{ ...bar('60%', 13), marginBottom: 32 }} />
      <div style={{ width: 100, height: 100, borderRadius: '50%', background: '#f0f0f0', marginBottom: 28 }} />
      {[1, 2, 3, 4].map(i => (
        <div key={i} style={{ marginBottom: 20 }}>
          <div style={bar('25%', 12)} />
          <div style={{ ...bar('100%', 44), marginBottom: 0 }} />
        </div>
      ))}
      <div style={bar('160px', 48)} />
    </div>
  );
}

// ── Main Account Page ──────────────────────────────────────
const Account = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const storedCustomer = (() => {
    try { return JSON.parse(localStorage.getItem('customer')); } catch { return null; }
  })();

  const customerId = storedCustomer?.id ?? null;

  React.useEffect(() => {
    if (!customerId) navigate('/login', { replace: true });
  }, [customerId, navigate]);

  const [activeTab, setActiveTab] = useState(location.state?.tab ?? 'Settings');
  const { data: customer, isLoading, isError } = useCustomer(customerId);

  if (!customerId) return null;

  const displayFirst = customer?.first_name ?? storedCustomer?.firstName ?? '';
  const displayLast = customer?.last_name ?? storedCustomer?.lastName ?? '';
  const displayEmail = customer?.email ?? storedCustomer?.email ?? '';

  return (
    <div className="acc-page">
      <div className="breadcrumb-bar">
        <div className="container">
          <div className="breadcrumb">
            <Link to="/">Home</Link><span>›</span><span>My Account</span>
          </div>
        </div>
      </div>
      <div className="container acc-container">
        <div className="acc-page-header">
          <h1 className="acc-page-title">Account</h1>
          <p className="acc-page-user">
            {displayFirst || displayLast ? <><strong>{displayFirst} {displayLast},</strong> </> : null}
            {displayEmail}
          </p>
        </div>
        <div className="acc-tabs">
          {TABS.map(tab => (
            <button key={tab} className={`acc-tab ${activeTab === tab ? 'acc-tab--active' : ''}`} onClick={() => setActiveTab(tab)}>
              {tab}
            </button>
          ))}
        </div>
        <div className="acc-content">
          {activeTab === 'Settings' && (
            isLoading ? <SettingsSkeleton /> :
              isError ? (
                <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 10, padding: '20px 24px', color: '#dc2626', fontSize: 14 }}>
                  ⚠️ Could not load your profile. Please refresh the page.
                </div>
              ) : <SettingsTab customer={customer} customerId={customerId} />
          )}
          {activeTab === 'Orders history' && (
            <OrdersTab customerId={customerId} />
          )}
        </div>
      </div>
    </div>
  );
};

export default Account;